export type InboxLocale = 'da' | 'en';

const DA_WEEKDAYS = ['søn.', 'man.', 'tirs.', 'ons.', 'tors.', 'fre.', 'lør.'] as const;
const EN_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DA_MONTHS = [
  'jan.',
  'feb.',
  'mar.',
  'apr.',
  'maj',
  'jun.',
  'jul.',
  'aug.',
  'sep.',
  'okt.',
  'nov.',
  'dec.',
] as const;
const EN_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function inboxLocale(language: string): InboxLocale {
  return language.toLowerCase().startsWith('en') ? 'en' : 'da';
}

function parts(iso: string): { date: Date; day: number; month: number; year: number; weekday: number; hours: number; minutes: number } {
  const date = new Date(iso);
  return {
    date,
    day: date.getDate(),
    month: date.getMonth(),
    year: date.getFullYear(),
    weekday: date.getDay(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function formatInboxClock(iso: string, locale: InboxLocale): string {
  const { hours, minutes } = parts(iso);
  const clock = `${pad2(hours)}${locale === 'da' ? '.' : ':'}${pad2(minutes)}`;
  return clock;
}

export function formatInboxListWhen(
  iso: string,
  locale: InboxLocale,
  now: Date = new Date(),
): string {
  const { date, day, month, year } = parts(iso);
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) return formatInboxClock(iso, locale);
  if (locale === 'da') {
    return year === now.getFullYear()
      ? `${day}. ${DA_MONTHS[month]}`
      : `${day}. ${DA_MONTHS[month]} ${year}`;
  }
  return year === now.getFullYear()
    ? `${day} ${EN_MONTHS[month]}`
    : `${day} ${EN_MONTHS[month]} ${year}`;
}

export function formatInboxBubbleWhen(iso: string, locale: InboxLocale): string {
  const { day, month, weekday } = parts(iso);
  const clock = formatInboxClock(iso, locale);
  if (locale === 'da') {
    return `${DA_WEEKDAYS[weekday]} ${day}. ${DA_MONTHS[month]} kl. ${clock}`;
  }
  return `${EN_WEEKDAYS[weekday]} ${day} ${EN_MONTHS[month]}, ${clock}`;
}

export function formatInboxAppointmentWhen(iso: string, locale: InboxLocale): string {
  const { day, month, weekday } = parts(iso);
  const clock = formatInboxClock(iso, locale);
  if (locale === 'da') {
    return `${DA_WEEKDAYS[weekday]} ${day}. ${DA_MONTHS[month]} kl. ${clock}`;
  }
  return `${EN_WEEKDAYS[weekday]} ${day} ${EN_MONTHS[month]}, ${clock}`;
}

export function previewMessageText(content: string, max = 120): string {
  const compact = content.replace(/\s+/g, ' ').trim();
  if (compact.length <= max) return compact;
  return `${compact.slice(0, max - 1).trimEnd()}…`;
}

export function conversationPreview(
  content: string,
  sender: 'patient' | 'clinic' | string,
  youPrefix: string,
): string {
  const text = previewMessageText(content);
  if (sender === 'clinic') return `${youPrefix}${text}`;
  return text;
}
